import React from 'react';
import { AlertTriangle, CheckCircle2, Clock3 } from 'lucide-react';
import { labelize } from '../components/ProposalItems';

function badgeTone(value) {
  if (value === true || value === 'intra_daypart_pairwise_cluster') {
    return 'border-emerald-200 bg-emerald-50 text-emerald-700';
  }
  if (value === false || String(value || '').startsWith('blocked')) {
    return 'border-red-200 bg-red-50 text-red-700';
  }
  return 'border-stone-200 bg-stone-50 text-stone-700';
}

function Badge({ value, children }) {
  const content = children || labelize(value);
  if (!content) return null;
  return (
    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium ${badgeTone(value)}`}>
      {content}
    </span>
  );
}

function BucketCounts({ counts }) {
  const entries = Object.entries(counts || {});
  if (!entries.length) return <span className="text-xs text-stone-400">No bucket spread</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {entries.map(([bucket, count]) => (
        <Badge key={bucket}>{labelize(bucket)} {count}</Badge>
      ))}
    </div>
  );
}

function fallbackCopy(row) {
  if (!row.row_tag_fallback_applied) return '';
  const requested = labelize(row.requested_tag_value || row.desired_tag_value);
  const actual = row.tag?.label || labelize(row.tag?.value);
  const reason = labelize(row.row_tag_fallback_reason || row.degradation_reason || 'no placeable row tag candidates');
  return `Requested ${requested}; showing ${actual} because ${reason}.`;
}

function durationCopy(duration) {
  if (!duration?.minutes) return '';
  const source = duration.source ? ` · ${labelize(duration.source)}` : '';
  return `${duration.minutes} min${source}`;
}

function slotDurationCopy(slot) {
  const budget = slot.duration_budget || {};
  if (!budget.slot_budget_minutes) return '';
  const stop = budget.row_addition_stop_reason ? ` · ${labelize(budget.row_addition_stop_reason)}` : '';
  return `${budget.used_minutes || 0}/${budget.slot_budget_minutes} min used · ${budget.remaining_minutes || 0} min open${stop}`;
}

function SlotPanel({ slot, onChangeRowTag, changingRowId }) {
  const distance = slot.distance_evaluation || {};
  const candidates = slot.candidates || [];
  const rowSlots = Array.isArray(slot.rows) ? slot.rows : [];
  return (
    <article className="rounded-lg border border-stone-200 bg-white p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="font-semibold text-stone-900">{slot.slot_label || labelize(slot.daypart)}</h3>
            <Badge value={distance.rule} />
            {slot.degraded && <Badge value={false}>{labelize(slot.degradation_reason || 'degraded')}</Badge>}
          </div>
          <p className="mt-1 text-xs text-stone-500">
            {rowSlots.length || slot.row_count || 0} rows · {slot.candidate_count || 0} shown from {slot.available_candidate_count || 0} available
          </p>
          {slotDurationCopy(slot) && (
            <p className="mt-1 inline-flex items-center gap-1 text-xs text-stone-500">
              <Clock3 className="h-3.5 w-3.5" />
              {slotDurationCopy(slot)}
            </p>
          )}
        </div>
        {distance.allowed ? (
          <CheckCircle2 className="h-5 w-5 text-emerald-600" />
        ) : (
          <AlertTriangle className="h-5 w-5 text-red-600" />
        )}
      </div>

      <div className="mt-3">
        <p className="mb-1 text-xs font-medium text-stone-500">Selected bucket spread</p>
        <BucketCounts counts={slot.selected_bucket_counts} />
      </div>

      {rowSlots.length ? (
        <div className="mt-3 space-y-3">
          {rowSlots.map(row => (
            <div key={row.row_id} className="rounded-md border border-stone-100 bg-stone-50 px-3 py-2">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-stone-500">Row {row.row_index}</p>
                  <p className="text-sm font-semibold text-stone-900">{row.tag?.label || labelize(row.tag?.value)}</p>
                  {row.row_tag_request_source === 'override' && (
                    <p className="mt-0.5 text-xs text-blue-700">Override: {labelize(row.requested_tag_value)}</p>
                  )}
                  {durationCopy(row.duration_estimate) && (
                    <p className="mt-0.5 text-xs text-stone-500">{durationCopy(row.duration_estimate)}</p>
                  )}
                </div>
                <div className="flex flex-wrap justify-end gap-1">
                  <Badge value={row.tag?.value} />
                  {row.row_tag_fallback_applied && <Badge value={false}>Substituted</Badge>}
                  {row.degraded && <Badge value={false}>{labelize(row.degradation_reason || 'degraded')}</Badge>}
                </div>
              </div>
              {onChangeRowTag && (row.available_tag_options || []).length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {(row.available_tag_options || []).map(option => {
                    const active = option.value === row.tag?.value && !row.row_tag_fallback_applied;
                    return (
                      <button
                        key={option.value}
                        type="button"
                        disabled={changingRowId === row.row_id || active}
                        onClick={() => onChangeRowTag(row, option.value)}
                        title={`${option.placeable_candidate_count ?? option.candidate_count ?? 0} placeable`}
                        className={`rounded-full border px-2 py-1 text-[11px] font-medium ${
                          active
                            ? 'border-stone-900 bg-stone-900 text-white'
                            : 'border-stone-300 bg-white text-stone-700 hover:border-stone-500 disabled:opacity-50'
                        }`}
                      >
                        {option.label}
                      </button>
                    );
                  })}
                </div>
              )}
              {fallbackCopy(row) && (
                <p className="mt-2 rounded-md border border-amber-200 bg-amber-50 p-2 text-xs text-amber-800">
                  {fallbackCopy(row)}
                </p>
              )}
              {row.selected_card && (
                <div className="mt-2 rounded-md bg-white px-3 py-2">
                  <p className="text-sm font-medium text-stone-900">{row.selected_card.card_title}</p>
                  <p className="text-xs text-stone-500">
                    {row.selected_card.place_master_id}
                    {durationCopy(row.selected_card.duration_estimate) ? ` · ${durationCopy(row.selected_card.duration_estimate)}` : ''}
                  </p>
                </div>
              )}
              <div className="mt-2 flex flex-wrap gap-1">
                {(row.candidates || []).slice(0, 6).map(card => (
                  <Badge key={card.candidate_id}>{card.card_title}</Badge>
                ))}
              </div>
              {!row.candidates?.length && (
                <p className="mt-2 rounded-md border border-amber-200 bg-amber-50 p-2 text-xs text-amber-800">
                  No candidates fit this row tag after daypart and distance rules.
                </p>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="mt-3 grid gap-2">
          {candidates.map(card => (
            <div key={card.candidate_id} className="rounded-md border border-stone-100 bg-stone-50 px-3 py-2">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                  <p className="text-sm font-medium text-stone-900">{card.card_title}</p>
                  <p className="text-xs text-stone-500">{card.place_master_id}</p>
                </div>
                <div className="flex flex-wrap justify-end gap-1">
                  <Badge value={card.bucket} />
                  <Badge value={card.slot_candidate?.daypart_match} />
                </div>
              </div>
              <div className="mt-2 flex flex-wrap gap-1">
                {(card.slot_candidate?.fit_sources || []).map(source => <Badge key={source} value={source} />)}
              </div>
            </div>
          ))}
        </div>
      )}
      <div className="mt-3 grid gap-2">
        {!candidates.length && (
          <p className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
            No candidates fit this slot after theme, daypart, and intra-daypart distance rules.
          </p>
        )}
      </div>
    </article>
  );
}

export default function ComposerSlotCandidates({ slots, onChangeRowTag, changingRowId }) {
  const rows = Array.isArray(slots) ? slots : [];
  if (!rows.length) return null;
  return (
    <section className="rounded-lg border border-stone-200 bg-stone-50 p-4">
      <h2 className="font-semibold text-stone-900">Slot Candidate Sets</h2>
      <p className="mt-1 text-sm text-stone-500">
        Ranked row carousel pools after row tag fit, daypart fit, and the 1.5 km intra-daypart cluster.
      </p>
      <div className="mt-4 grid gap-3 lg:grid-cols-2">
        {rows.map(slot => (
          <SlotPanel
            key={slot.slot_id}
            slot={slot}
            onChangeRowTag={onChangeRowTag}
            changingRowId={changingRowId}
          />
        ))}
      </div>
    </section>
  );
}
